/**
 * PropertyEditor - Edit properties of the selected component
 *
 * Features:
 * - Type-specific property fields
 * - Label, prompt, instructions editing
 * - Answer key configuration
 * - Curriculum outcome picker
 * - Points and grading settings
 * - Crop region configuration
 */

import { useState, useMemo, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Settings2,
  Target,
  Plus,
  X,
  Search,
  GraduationCap,
  FileCheck,
  Crop,
} from "lucide-react";
import type { WorksheetComponent, CropRegion } from "./types";
import type { CurriculumOutcome } from "@/components/planner/types";

// ============================================================================
// Outcome Picker (similar to BlockDetail)
// ============================================================================

interface OutcomePickerProps {
  outcomes: CurriculumOutcome[];
  selectedIds: string[];
  onToggle: (outcomeId: string) => void;
}

function OutcomePicker({ outcomes, selectedIds, onToggle }: OutcomePickerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [subjectFilter, setSubjectFilter] = useState<string>("all");
  const [gradeFilter, setGradeFilter] = useState<string>("all");

  const subjects = useMemo(() => {
    const unique = new Set(outcomes.map((o) => o.subject));
    return Array.from(unique).sort();
  }, [outcomes]);

  const grades = useMemo(() => {
    const unique = new Set(outcomes.map((o) => o.grade));
    return Array.from(unique).sort((a, b) => {
      if (a === "Kindergarten") return -1;
      if (b === "Kindergarten") return 1;
      const numA = parseInt(a.replace("Grade ", ""));
      const numB = parseInt(b.replace("Grade ", ""));
      return numA - numB;
    });
  }, [outcomes]);

  const filteredOutcomes = useMemo(() => {
    return outcomes.filter((outcome) => {
      if (subjectFilter !== "all" && outcome.subject !== subjectFilter) {
        return false;
      }
      if (gradeFilter !== "all" && outcome.grade !== gradeFilter) {
        return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          outcome.outcome_text.toLowerCase().includes(query) ||
          outcome.organizing_idea.toLowerCase().includes(query) ||
          outcome.id.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [outcomes, subjectFilter, gradeFilter, searchQuery]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search outcomes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Select value={subjectFilter} onValueChange={setSubjectFilter}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Subjects</SelectItem>
              {subjects.map((subject) => (
                <SelectItem key={subject} value={subject}>
                  {subject}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={gradeFilter} onValueChange={setGradeFilter}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Grade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Grades</SelectItem>
              {grades.map((grade) => (
                <SelectItem key={grade} value={grade}>
                  {grade}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <ScrollArea className="h-[200px] border rounded-md">
        <div className="p-2 space-y-1">
          {filteredOutcomes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No outcomes found
            </div>
          ) : (
            filteredOutcomes.map((outcome) => (
              <label
                key={outcome.id}
                className={cn(
                  "flex items-start gap-2 p-2 rounded-md cursor-pointer",
                  "hover:bg-muted transition-colors text-sm",
                  selectedIds.includes(outcome.id) && "bg-muted"
                )}
              >
                <Checkbox
                  checked={selectedIds.includes(outcome.id)}
                  onCheckedChange={() => onToggle(outcome.id)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{outcome.id}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">
                    {outcome.outcome_text}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ============================================================================
// Options Editor (for circle_options)
// ============================================================================

interface OptionsEditorProps {
  options: string[];
  onChange: (options: string[]) => void;
}

function OptionsEditor({ options, onChange }: OptionsEditorProps) {
  const handleChange = useCallback(
    (index: number, value: string) => {
      const newOptions = [...options];
      newOptions[index] = value;
      onChange(newOptions);
    },
    [options, onChange]
  );

  const handleAdd = useCallback(() => {
    onChange([...options, `Option ${options.length + 1}`]);
  }, [options, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      const newOptions = options.filter((_, i) => i !== index);
      onChange(newOptions);
    },
    [options, onChange]
  );

  return (
    <div className="space-y-2">
      {options.map((option, index) => (
        <div key={index} className="flex gap-2">
          <Input
            value={option}
            onChange={(e) => handleChange(index, e.target.value)}
            placeholder={`Option ${index + 1}`}
          />
          {options.length > 2 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemove(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={handleAdd} className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        Add Option
      </Button>
    </div>
  );
}

// ============================================================================
// Crop Region Editor
// ============================================================================

interface CropRegionEditorProps {
  cropRegion?: CropRegion;
  onChange: (cropRegion: CropRegion) => void;
}

function CropRegionEditor({ cropRegion, onChange }: CropRegionEditorProps) {
  const defaultRegion: CropRegion = cropRegion || {
    left: 0,
    top: 0,
    right: 1,
    bottom: 1,
  };

  const handleChange = useCallback(
    (field: keyof CropRegion, value: string) => {
      const numValue = parseFloat(value) || 0;
      const clampedValue = Math.max(0, Math.min(1, numValue));
      onChange({
        ...defaultRegion,
        [field]: clampedValue,
      });
    },
    [defaultRegion, onChange]
  );

  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1">
        <Label className="text-xs">Left (0-1)</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          max="1"
          value={defaultRegion.left}
          onChange={(e) => handleChange("left", e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Top (0-1)</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          max="1"
          value={defaultRegion.top}
          onChange={(e) => handleChange("top", e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Right (0-1)</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          max="1"
          value={defaultRegion.right}
          onChange={(e) => handleChange("right", e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Bottom (0-1)</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          max="1"
          value={defaultRegion.bottom}
          onChange={(e) => handleChange("bottom", e.target.value)}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export interface PropertyEditorProps {
  component: WorksheetComponent | null;
  outcomes: CurriculumOutcome[];
  onUpdate: (id: string, updates: Partial<WorksheetComponent>) => void;
  className?: string;
}

export function PropertyEditor({
  component,
  outcomes,
  onUpdate,
  className,
}: PropertyEditorProps) {
  const [outcomePickerOpen, setOutcomePickerOpen] = useState(false);

  const handleChange = useCallback(
    <K extends keyof WorksheetComponent>(
      field: K,
      value: WorksheetComponent[K]
    ) => {
      if (component) {
        onUpdate(component.id, { [field]: value });
      }
    },
    [component, onUpdate]
  );

  const handleOutcomeToggle = useCallback(
    (outcomeId: string) => {
      if (component) {
        const currentIds = component.outcomeIds || [];
        const newIds = currentIds.includes(outcomeId)
          ? currentIds.filter((id) => id !== outcomeId)
          : [...currentIds, outcomeId];
        onUpdate(component.id, { outcomeIds: newIds });
      }
    },
    [component, onUpdate]
  );

  const handleRemoveOutcome = useCallback(
    (outcomeId: string) => {
      if (component) {
        const newIds = (component.outcomeIds || []).filter(
          (id) => id !== outcomeId
        );
        onUpdate(component.id, { outcomeIds: newIds });
      }
    },
    [component, onUpdate]
  );

  const selectedOutcomes = useMemo(() => {
    if (!component?.outcomeIds) return [];
    const idsSet = new Set(component.outcomeIds);
    return outcomes.filter((o) => idsSet.has(o.id));
  }, [component?.outcomeIds, outcomes]);

  // Empty state
  if (!component) {
    return (
      <div
        className={cn(
          "flex flex-col h-full border-l bg-muted/30 w-80",
          className
        )}
      >
        <div className="p-4 border-b">
          <h2 className="font-semibold text-sm">Properties</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Select a component to edit
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center p-4">
            <Settings2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No component selected</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col h-full border-l bg-muted/30 w-80",
        className
      )}
    >
      <div className="p-4 border-b">
        <h2 className="font-semibold text-sm flex items-center gap-2">
          <Settings2 className="h-4 w-4" />
          Properties
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Editing: {component.type.replace("_", " ")}
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Basic Properties */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="label">Label</Label>
              <Input
                id="label"
                value={component.label}
                onChange={(e) => handleChange("label", e.target.value)}
                placeholder="Component label"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt">Prompt / Question</Label>
              <Textarea
                id="prompt"
                value={component.prompt}
                onChange={(e) => handleChange("prompt", e.target.value)}
                placeholder="Enter the prompt text..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions">Instructions (optional)</Label>
              <Input
                id="instructions"
                value={component.instructions || ""}
                onChange={(e) => handleChange("instructions", e.target.value)}
                placeholder="Additional instructions..."
              />
            </div>
          </div>

          <Separator />

          {/* Options (for circle_options) */}
          {component.type === "circle_options" && (
            <>
              <div className="space-y-2">
                <Label>Answer Options</Label>
                <OptionsEditor
                  options={component.options || []}
                  onChange={(options) => handleChange("options", options)}
                />
              </div>
              <Separator />
            </>
          )}

          {/* Grading */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <FileCheck className="h-4 w-4" />
              Grading
            </h3>

            <div className="space-y-2">
              <Label htmlFor="points">Points</Label>
              <Input
                id="points"
                type="number"
                min="0"
                step="1"
                value={component.points || 0}
                onChange={(e) =>
                  handleChange("points", parseInt(e.target.value) || 0)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="autoGradable" className="cursor-pointer">
                Auto-gradable
              </Label>
              <Switch
                id="autoGradable"
                checked={component.autoGradable || false}
                onCheckedChange={(checked) =>
                  handleChange("autoGradable", checked)
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="answerKeyType">Answer Key Type</Label>
              <Select
                value={component.answerKeyType || "static"}
                onValueChange={(value) =>
                  handleChange(
                    "answerKeyType",
                    value as "static" | "dynamic" | "teacher_review"
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">Static (fixed answer)</SelectItem>
                  <SelectItem value="dynamic">Dynamic (varies)</SelectItem>
                  <SelectItem value="teacher_review">Teacher Review</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {component.answerKeyType !== "teacher_review" && (
              <div className="space-y-2">
                <Label htmlFor="answerKey">Answer Key</Label>
                <Input
                  id="answerKey"
                  value={
                    typeof component.answerKey === "string"
                      ? component.answerKey
                      : Array.isArray(component.answerKey)
                      ? component.answerKey.join(", ")
                      : ""
                  }
                  onChange={(e) => handleChange("answerKey", e.target.value)}
                  placeholder="Correct answer(s)..."
                />
                <p className="text-xs text-muted-foreground">
                  For multiple answers, separate with commas
                </p>
              </div>
            )}

            {component.answerKeyType === "dynamic" && (
              <div className="space-y-2">
                <Label htmlFor="answerKeyDescription">Answer Description</Label>
                <Textarea
                  id="answerKeyDescription"
                  value={component.answerKeyDescription || ""}
                  onChange={(e) =>
                    handleChange("answerKeyDescription", e.target.value)
                  }
                  placeholder="Describe how the answer is determined..."
                  rows={2}
                />
              </div>
            )}
          </div>

          <Separator />

          {/* Extraction (for auto-grading) */}
          {component.autoGradable && (
            <>
              <div className="space-y-4">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Crop className="h-4 w-4" />
                  Extraction Config
                </h3>

                <div className="space-y-2">
                  <Label htmlFor="extractionPrompt">Extraction Prompt</Label>
                  <Textarea
                    id="extractionPrompt"
                    value={component.extractionPrompt || ""}
                    onChange={(e) =>
                      handleChange("extractionPrompt", e.target.value)
                    }
                    placeholder="Instructions for AI extraction..."
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Instructions for the AI to extract the student's answer
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Crop Region</Label>
                  <CropRegionEditor
                    cropRegion={component.cropRegion}
                    onChange={(region) => handleChange("cropRegion", region)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Define the area on the page to extract (0-1 scale)
                  </p>
                </div>
              </div>

              <Separator />
            </>
          )}

          {/* Curriculum Outcomes */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Curriculum Outcomes
              </h3>
              <Popover open={outcomePickerOpen} onOpenChange={setOutcomePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-3" align="end">
                  <OutcomePicker
                    outcomes={outcomes}
                    selectedIds={component.outcomeIds || []}
                    onToggle={handleOutcomeToggle}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {selectedOutcomes.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedOutcomes.map((outcome) => (
                  <Badge
                    key={outcome.id}
                    variant="secondary"
                    className="pr-1 flex items-center gap-1"
                  >
                    <span
                      className="truncate max-w-[120px]"
                      title={outcome.outcome_text}
                    >
                      {outcome.id}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => handleRemoveOutcome(outcome.id)}
                      aria-label={`Remove ${outcome.id}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No outcomes linked. Add outcomes to track student progress.
              </p>
            )}
          </div>

          {/* Position (read-only info) */}
          <Separator />
          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Position
            </h3>
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>
                X: {(component.position.x * 100).toFixed(0)}%
              </div>
              <div>
                Y: {(component.position.y * 100).toFixed(0)}%
              </div>
              <div>
                W: {(component.position.width * 100).toFixed(0)}%
              </div>
              <div>
                H: {(component.position.height * 100).toFixed(0)}%
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Drag component on canvas to reposition
            </p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
